/*!

=========================================================
* Light Bootstrap Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/light-bootstrap-dashboard-pro-react
* Copyright 2019 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, {Component} from "react";
import {Col, Row} from "react-bootstrap";
import {validateAll} from 'indicative/validator';
import Card from "../../../components/Cards/Card/Card.jsx";
import FormInputs from "../../../components/Common/FormInputs.jsx";
import {API} from "lib/API";
import Footer from "../../../components/Footer/Footer";
import {withTranslation} from 'react-i18next';
import KnoaBreadcrumb from "../../../components/Common/KnoaBreadcrumb";
import {mauiRoutes} from "../../../routes";
import {capitalizeWords} from "../../../lib/common";
import CommonContainer from "../../../components/Common/CommonContainer";
import Spinner from "../../../components/Common/Spinner";
import {Redirect} from "react-router-dom";
import ModalDialog from "../../../components/Common/ModalDialog";
import {
    getInfoMessages,
    getSpecificOPErrorMessages
} from "../../../lib/notifications/messageUtils";
import {notifyWithMessage} from "../../../lib/notifications/notifyWithMessage";
import {Helmet} from "react-helmet";

const localStorage = require('../../../lib/localStorage');

const FormErrors = ({ formErrors, fieldName }) => {
    const errorMessage = formErrors[fieldName];
    if (!errorMessage) return null;

    return (
        <div
            id={`${fieldName}Error`}
            className="row formErrors"
            aria-live="polite"
        >
            <p className="form-validation-error">{errorMessage}</p>
        </div>
    );
};

class UserAccount extends Component {
    constructor(prop) {
        super(prop);

        this.mainContent = React.createRef();

        this.state = {
            firstName: '',
            lastName: '',
            email: '',
            orignalEmail: '',
            showEmailChange: false,
            knoaRoles: [],
            role: '',
            proxyUserMode: true,
            licenses: [],   // no use will delete
            userId: this.props.location.state ? this.props.location.state.id : -1,
            formErrors: {},
            inputClass_firstName: 'form-control',
            inputClass_lastName: 'form-control',
            inputClass_email: 'form-control',
            fetchInProgress: "false",
            redirect: false,
            loaded: false,
            disableSaveButton: false,
            showLoggedoutModal: false,
            sessionTerminated: false,
            setMainFocus: false
        };
    }

    submitData() {
        this.setState({fetchInProgress: "true"});

        const createUser = this.props.location.state?.createUser || false;
        const userSubmitURL = createUser
            ? "/admin/user/create"
            : `/admin/user/edit/${this.state.userId}`;

        const payload = {
            firstName: this.state.firstName,
            lastName: this.state.lastName,
            email: this.state.email,
            role: this.state.role,
            needSupport: this.state.proxyUserMode
        };

        API.admin
            .post(userSubmitURL, payload)
            .then(resp => {
                const { resultCode, inSupport = false } = resp.data || {};

                if (createUser) {
                    if (resultCode === 200) {
                        notifyWithMessage({
                            type: "success",
                            t: this.props.t,
                            operation: "generic",
                            parameter: "a new user was created!",
                            inSupport
                        });
                    } else {
                        notifyWithMessage({
                            type: "error",
                            t: this.props.t,
                            operation: "genericPleaseTryAgain",
                            parameter: "A new user was not created!",
                            resultCode
                        });
                    }
                    this.setState({ fetchInProgress: false, redirect: true });
                } else {
                    if (resultCode === 200) {
                        this.setState({ orginalEmail: this.state.email });
                        notifyWithMessage({
                            type: "success",
                            t: this.props.t,
                            operation: "changeSaved",
                            parameter: "User account",
                            inSupport
                        });
                    } else if (resultCode === 2004) {
                        notifyWithMessage({
                            type: "error",
                            t: this.props.t,
                            operation: "generic",
                            parameter: "There are not enough licenses for this role.",
                            resultCode
                        });
                    } else {
                        notifyWithMessage({
                            type: "error",
                            t: this.props.t,
                            operation: "changeNotSaved",
                            parameter: "User account",
                            resultCode
                        });
                    }
                    this.setState({ fetchInProgress: false });
                }
            })
            .catch(err => {
                console.error(err);
                this.setState({ fetchInProgress: false });

                if (localStorage.get("accessDenied401") === "true") {
                    this.setState({ showLoggedoutModal: true });
                    return;
                }

                const resultCode = err?.data?.resultCode ?? -1;

                if (err.status === 401 || resultCode === 6007) {
                    window.location.href = "/#/auth/logout";
                } else if (resultCode > -1) {
                    notifyWithMessage({ type: "error", t: this.props.t, resultCode, isNormal: true });
                } else {
                    notifyWithMessage({ type: "error", t: this.props.t, errorObject: err, isNormal: true });
                }
            });
    }

    handleSubmit = (e) => {
        e.preventDefault();
        localStorage.set("proceedToSave", "true");

        const createUser = this.props.location.state
            ? this.props.location.state.createUser
            : false;

        const data = this.state;

        // Rules are the same regardless of create/edit
        const rules = {
            firstName: "required|string",
            lastName: "required|string",
            email: "required|email"
        };

        const messages = {
            "firstName.required": this.props.t("validation:fieldRequired", { field: "firstName" }),
            "lastName.required": this.props.t("validation:fieldRequired", { field: "lastName" }),
            "email.required": this.props.t("validation:fieldRequired", { field: "email" }),
            "email.email": this.props.t("validation:fieldValidationFail", { field: "email" })
        };

        validateAll(data, rules, messages,)
            .then(() => {
                const emailChanged = !createUser && this.state.email !== this.state.orginalEmail;

                this.setState({
                    formErrors: '',
                    inputClass_firstName: 'form-control',
                    inputClass_lastName: 'form-control',
                    inputClass_email: 'form-control',
                    showEmailChange: emailChanged,
                    disableSaveButton: emailChanged
                });

                // keep localStorage in sync
                localStorage.set("proceedToSave", emailChanged ? "false" : "true");

                // only submit if we're not in the "email changed" branch
                if (!emailChanged && localStorage.get("proceedToSave") === "true") {
                    this.submitData();
                }
            })
            .catch(errors => {
                const formattedErrors = {};
                const errorFields = new Set(errors.map(err => err.field));

                // Build input class map
                const inputClasses = {
                    inputClass_firstName: errorFields.has('firstName')
                        ? 'form-control form-validation-input-error'
                        : 'form-control',
                    inputClass_lastName: errorFields.has('lastName')
                        ? 'form-control form-validation-input-error'
                        : 'form-control',
                    inputClass_email: errorFields.has('email')
                        ? 'form-control form-validation-input-error'
                        : 'form-control'
                };

                // Collect error messages
                errors.forEach(err => {
                    formattedErrors[err.field] = err.message;
                });

                this.setState({
                    ...inputClasses,
                    formErrors: formattedErrors
                });

                notifyWithMessage({
                    type: "error",
                    t: this.props.t,
                    operation: "validationErrorMessage",
                    parameter: null,
                    resultCode: null
                });
            });
    };

    updateRole = (e) => {
        this.setState({role: e.target.value});
    };

    updateProxyUserMode = (e) => {
        let enabled = e.target.value === 'Enabled';
        this.setState({proxyUserMode: enabled});
    };

    updateProxyUserModeForEditUser = (e) => {
        let enabled = e.target.value === 'true';
        this.setState({proxyUserMode: enabled});
    };

    handleChange = (e) => {
        e.preventDefault();
        this.setState({[e.target.name]: e.target.value});
    };

    handleEmailChangeConfirm = () => {
        let tempFormErrors = this.state.formErrors;
        if (tempFormErrors !== '') tempFormErrors['email'] = '';
        localStorage.set("proceedToSave", "true");
        this.setState({
            showEmailChange: false,
            formErrors: tempFormErrors,
            inputClass_email: 'form-control',
            disableSaveButton: false,
        })
        this.submitData();
    };

    handleClose = () => {
        let tempFormErrors = this.state.formErrors;
        if (tempFormErrors !== '') tempFormErrors['email'] = '';
        localStorage.set("proceedToSave", "false");
        this.setState({
            showEmailChange: false,
            email: this.state.orginalEmail,
            formErrors: tempFormErrors,
            disableSaveButton: false
        });
    };

    handleLogoutConfirm = () =>{
        this.setState({sessionTerminated: true});
    };


    componentDidMount() {
        let userURL;
        let createUser = this.props.location.state ? this.props.location.state.createUser : false;
        let editUser = this.props.location.state ? this.props.location.state.linkName : "";

        if (createUser) userURL = '/admin/user/view/create';
        else if (editUser === "editUser") userURL = `/admin/user/view/edit/${this.state.userId}`;
        else userURL = '/user/info';


        API.project
            .get(userURL)
            .then(resp => {
                const {data} = resp.data;

                let firstName;
                let lastName;
                let email;
                let role;
                let knoaRoles = [];
                let proxyUserMode = true;

                if (createUser) {
                    firstName = "";
                    lastName = "";
                    email = "";
                    role = data.knoaRoles[0];
                    knoaRoles = data.knoaRoles;
                    proxyUserMode = true;
                } else if (editUser === "editUser") {
                    firstName = data.userInfo.firstName;
                    lastName = data.userInfo.lastName;
                    email = data.userInfo.email;
                    role = data.userInfo.role;
                    knoaRoles = data.knoaRoles;
                    proxyUserMode = data.userInfo.needSupport;
                } else {
                    firstName = data.firstName;
                    lastName = data.lastName;
                    role = data.role;
                    email = data.email;
                }

                this.setState({
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    orginalEmail: email,
                    role: role,
                    knoaRoles: knoaRoles,
                    proxyUserMode: proxyUserMode,
                    loaded: true,
                    setMainFocus: true
                });
            })
            .catch(e => {
                console.error(e);
                if(localStorage.get("accessDenied401") === "true"){
                    this.setState({showLoggedoutModal:true});
                    return;
                }
                let resultCode = -1;
                if(e.data) {
                    resultCode = e.data.resultCode;
                }
                if (e.status === 401 || resultCode === 6007)
                    window.location.href = "/#/auth/logout"

                if (resultCode > -1) {
                    notifyWithMessage({type: 'error', t: this.props.t, resultCode, isNormal: true});
                } else {
                    notifyWithMessage({type: 'error', t: this.props.t, errorObject: e, isNormal: true});
                }
            });
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if(this.state.setMainFocus) {
            if (this.mainContent && this.mainContent.current) {
                this.mainContent.current.focus({ preventScroll: true });
                this.setState({setMainFocus: false})
            }
        }
    }

    render() {
        if(this.state.sessionTerminated)
            return <Redirect to={{pathname: '/auth/logout'}}/>;

        let showLoggedOutWindow = this.state.showLoggedoutModal;

        if (!this.state.loaded && !showLoggedOutWindow) {
            return null;
        }

        const {t} = this.props;
        let routes = mauiRoutes.getRoutes();
        const footerObj = [];

        let createUser = this.props.location.state ? this.props.location.state.createUser : false;
        let editUser = this.props.location.state ? this.props.location.state.linkName : "";

        if (createUser && this.state.redirect) {
            return <Redirect to='/admin/Settings/UserManagement'/>;
        }

        let disableButton = this.state.disableSaveButton;
        if (createUser || editUser === "editUser") {
            const updatePreferencesObj = {
                name: this.props.t('common:save'),
                callback: this.handleSubmit,
                disabled: disableButton
            };
            footerObj.push(updatePreferencesObj);
        }

        return (
            <div ref={this.mainContent} tabIndex={"-1"}>
                {showLoggedOutWindow && (
                <ModalDialog
                    showModal={showLoggedOutWindow}
                    modalID="mUserLogOut"
                    title={t("common:logOut")}
                    contentBody={
                        <span>{getSpecificOPErrorMessages(this.props.t,"logoutfor401",null,null)}</span>
                    }
                    bodyPaddingBottom='15px'
                    footerBtnBsStyle="success"
                    footerBtnClass="KnoaSuccessButton"
                    footerBtnOnClick={this.handleLogoutConfirm}
                    footerBtnText={this.props.t('common:ok')}
                />
                )}
                {(editUser === "editUser" && this.state.showEmailChange) && (
                <ModalDialog
                    showModal={this.state.showEmailChange}
                    modalID="mUserEmail"
                    title={t("adminSettings:emailChange")}
                    contentBody={
                        <span>{getInfoMessages(this.props.t, 'changeEmail', 'email', this.state.orginalEmail)}</span>
                    }                    
                    bodyPaddingBottom='15px'
                    footerBtnBsStyle="danger"
                    footerBtnClass="KnoaDangerButton"
                    footerBtnOnClick={this.handleEmailChangeConfirm}
                    footerBtnText={this.props.t('common:yes')}
                    footerBtnCloseText={this.props.t('common:no')}
                    handleClose={this.handleClose}
                />
                )}
                {(createUser || editUser === "editUser") && <form onSubmit={this.handleSubmit} noValidate>
                    <div className="main-content main-content-page-with-footer">
                        <Helmet>
                            {createUser && <title>Knoa Analytics - New User</title>}
                            {!createUser && (editUser === "editUser") && <title>Knoa Analytics - Edit User</title>}
                        </Helmet>
                        <Row className="user-admin-breadcrumb">
                            <KnoaBreadcrumb
                                href="#/admin/AllSettings"
                                href2="#/admin/Settings/UserManagement"
                                firstLevelText={t("common:settings")}
                                secondLevelText={t("route.name:User Management")}
                                thirdLevelText={(this.props.navVarBrand) ? this.props.navVarBrand : this.props.getActiveRoute(routes, t)}
                            />
                        </Row>
                        {this.state.fetchInProgress === "true" && <Spinner/>}
                        <div hidden={this.state.fetchInProgress === "true"}>
                            <CommonContainer className="common-page-content-row user-profile-rule">
                                <Col className="knoa-region" tabIndex={"-1"}>
                                    <Card
                                        title={t('user:userAccount')}
                                        content={
                                            <>
                                                <div className="col-md-12">
                                                    <FormInputs
                                                        ncols={["col-md-6", "col-md-6"]}
                                                        proprieties={[
                                                            {
                                                                label: t('user:firstName'),
                                                                type: "text",
                                                                name: "firstName",
                                                                className: this.state.inputClass_firstName,
                                                                placeholder: capitalizeWords(t('user:firstName')),
                                                                value: this.state.firstName,
                                                                disabled: false,
                                                                onChange: this.handleChange,
                                                                id: "firstName",
                                                                required: true,
                                                                "aria-invalid": !!this.state.formErrors.firstName,
                                                                "aria-describedby": this.state.formErrors.firstName ? "firstNameError" : undefined
                                                            },
                                                            {
                                                                label: t('user:lastName'),
                                                                type: "text",
                                                                name: "lastName",
                                                                className: this.state.inputClass_lastName,
                                                                placeholder: capitalizeWords(t('user:lastName')),
                                                                value: this.state.lastName,
                                                                disabled: false,
                                                                onChange: this.handleChange,
                                                                id: "lastName",
                                                                required: true,
                                                                "aria-invalid": !!this.state.formErrors.lastName,
                                                                "aria-describedby": this.state.formErrors.lastName ? "lastNameError" : undefined
                                                            }
                                                        ]}
                                                    />
                                                    <Row>
                                                        <div className="col-md-6">
                                                            <FormErrors formErrors={this.state.formErrors} fieldName="firstName"/>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <FormErrors formErrors={this.state.formErrors} fieldName="lastName"/>
                                                        </div>
                                                    </Row>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div className="form-group">
                                                                <label className="control-label" htmlFor="email">{t('user:email')} <span className="text-danger-1">*</span></label>
                                                                <input name="email"
                                                                       id="email"
                                                                       type="email"
                                                                       placeholder={capitalizeWords(t('user:email'))}
                                                                       onChange={this.handleChange}
                                                                       className={this.state.inputClass_email}
                                                                       value={this.state.email}
                                                                       required
                                                                       aria-invalid={!!this.state.formErrors.email}
                                                                       aria-describedby={this.state.formErrors.email ? "emailError" : undefined}
                                                                />

                                                            </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                            <div className="form-group">
                                                                <label className="control-label" htmlFor="role">{t('user:analyticsRole')}</label>
                                                                {createUser &&
                                                                <select onChange={this.updateRole} className="form-control" name="role" id="role">
                                                                    {this.state.knoaRoles.map((role, index) => (
                                                                        <option key={role || index} value={role}>
                                                                            {role}
                                                                        </option>
                                                                    ))}
                                                                </select>}
                                                                {(editUser === "editUser") && <select
                                                                    name="role"
                                                                    id="role"
                                                                    disabled={this.state.role === "Admin"}
                                                                    onChange={this.updateRole}
                                                                    className="form-control"
                                                                    value={this.state.role}>
                                                                    {this.state.role === "Admin" && <option value={this.state.role}
                                                                                                            key={0}>{this.state.role}</option>}
                                                                    {this.state.role !== "Admin" && this.state.knoaRoles.map((role, index) =>
                                                                        <option value={role}
                                                                                key={index}>{role}</option>)}
                                                                </select>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-12">
                                                        <FormErrors formErrors={this.state.formErrors} fieldName="email"/>
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <div className="form-group">
                                                                <label
                                                                    className="control-label" htmlFor="mode">{t('user:proxyUserMode')}</label>
                                                                {createUser && <select
                                                                    onChange={this.updateProxyUserMode}
                                                                    name="mode"
                                                                    id="mode"
                                                                    className="form-control">
                                                                    <option value={'Enabled'}>Enabled</option>
                                                                    <option value={'Disabled'}>Disabled</option>
                                                                </select>}
                                                                {(editUser === "editUser") && <select
                                                                    onChange={this.updateProxyUserModeForEditUser}
                                                                    name="mode"
                                                                    id="mode"
                                                                    className="form-control"
                                                                    value={this.state.proxyUserMode}>
                                                                    <option value={true}>Enabled</option>
                                                                    <option value={false}>Disabled</option>
                                                                </select>
                                                                }

                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="clearfix"/>
                                            </>
                                        }
                                    />
                                </Col>
                            </CommonContainer>
                        </div>
                    </div>
                    <Footer fluid footerObj={footerObj}/>
                </form>}
                {!(createUser || editUser === "editUser") &&
                <div className="main-content">
                    <Helmet>
                        <title>Knoa Analytics - My Profile</title>
                    </Helmet>
                    {this.state.fetchInProgress === "true" && <Spinner/>}
                    <div hidden={this.state.fetchInProgress === "true"}>
                        <CommonContainer className="common-page-content-row user-profile-rule">
                            <Col className="knoa-region" tabIndex={"-1"}>
                                <Card
                                    title={t("user:userAccount")}
                                    content={
                                        <>
                                            <FormInputs
                                                ncols={["col-md-6", "col-md-6"]}
                                                proprieties={[
                                                    {
                                                        label: t("user:firstName"),
                                                        type: "text",
                                                        className: "form-control",
                                                        placeholder: "firstname",
                                                        value: this.state.firstName,
                                                        disabled: true,
                                                        id: "firstname",
                                                        name: "firstname"
                                                    },
                                                    {
                                                        label: t("user:lastName"),
                                                        type: "text",
                                                        className: "form-control",
                                                        placeholder: "lastname",
                                                        value: this.state.lastName,
                                                        disabled: true,
                                                        id: "lastname",
                                                        name: "lastname"
                                                    }
                                                ]}
                                            />
                                            <FormInputs
                                                ncols={["col-md-6", "col-md-6"]}
                                                proprieties={[
                                                    {
                                                        label: t("user:username") + " (" + t("user:emailAddress") + ")",
                                                        type: "email",
                                                        className: "form-control",
                                                        placeholder: "username",
                                                        value: this.state.email,
                                                        disabled: true,
                                                        id: "username",
                                                        name: "username"
                                                    },
                                                    {
                                                        label: t("user:analyticsRole"),
                                                        type: "text",
                                                        className: "form-control",
                                                        placeholder: "knoarole",
                                                        value: this.state.role,
                                                        disabled: true,
                                                        id: "role",
                                                        name: "role"
                                                    }
                                                ]}
                                            />
                                            <div className="clearfix"/>
                                        </>
                                    }
                                />
                            </Col>
                        </CommonContainer>
                    </div>
                </div>
                }
            </div>
        );
    }
}

export default withTranslation()(UserAccount);
